(() => {
  const supportedCurrencies = new Set(['INR', 'USD', 'GBP', 'EUR', 'JPY']);
  const europeanCountries = new Set([
    'AT',
    'BE',
    'BG',
    'CY',
    'CZ',
    'DE',
    'DK',
    'EE',
    'ES',
    'FI',
    'FR',
    'GR',
    'HR',
    'HU',
    'IE',
    'IT',
    'LT',
    'LU',
    'LV',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SE',
    'SI',
    'SK',
  ]);
  const manualCurrencyKey = 'iroiro_manual_currency';
  const geoCurrencyKey = 'iroiro_geo_currency';
  const novaCurrencyKey = 'bacurr_user_cur';
  const geoCacheMs = 24 * 60 * 60 * 1000;
  const defaultCurrency = 'USD';
  const cartHtmlUpdatedEvent = 'iroiro:cart-html-updated';
  const currencyMarkupUpdatedEvent = 'iroiro:currency-markup-updated';
  const currencyUpdatedEvent = 'iroiro:currency-updated';
  const currencyPendingTimeout = 4000;
  let currencySelectionId = 0;
  let pendingCurrencyCleanup = null;

  const normalizeCurrency = (currency) => {
    const normalized = typeof currency === 'string' ? currency.trim().toUpperCase() : '';
    return supportedCurrencies.has(normalized) ? normalized : null;
  };

  const readStorageValue = (storage, key) => {
    try {
      const rawValue = storage.getItem(key);
      if (!rawValue) return null;

      try {
        return JSON.parse(rawValue);
      } catch (_error) {
        return rawValue;
      }
    } catch (_error) {
      return null;
    }
  };

  const writeStorageValue = (storage, key, value) => {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch (_error) {
      return false;
    }

    return true;
  };

  const readStoredCurrency = (storage, key) => normalizeCurrency(readStorageValue(storage, key));

  const readSelectedCurrency = () => {
    const manualCurrency = readStoredCurrency(localStorage, manualCurrencyKey);
    if (manualCurrency) return manualCurrency;

    return readStoredCurrency(sessionStorage, novaCurrencyKey);
  };

  const readCachedGeoCurrency = () => {
    const cached = readStorageValue(localStorage, geoCurrencyKey);

    if (!cached || typeof cached !== 'object') return null;
    if (typeof cached.expiresAt !== 'number' || cached.expiresAt <= Date.now()) return null;

    return normalizeCurrency(cached.currency);
  };

  const readDisplayedCurrency = () => {
    const novaSelectorCurrency = normalizeCurrency(document.querySelector('#baCurrSelector')?.value);
    if (novaSelectorCurrency) return novaSelectorCurrency;

    return readSelectedCurrency() || readCachedGeoCurrency();
  };

  const readRequestedCurrency = () => readSelectedCurrency() || readCachedGeoCurrency() || readDisplayedCurrency();

  const dispatchCurrencyUpdate = (currency, confirmed = false) => {
    const normalizedCurrency = normalizeCurrency(currency);

    if (!normalizedCurrency) return;

    document.dispatchEvent(
      new CustomEvent(currencyUpdatedEvent, {
        detail: { currency: normalizedCurrency, confirmed },
      })
    );
  };

  const updateCartCurrencyDisclosure = (currency = readDisplayedCurrency()) => {
    const normalizedCurrency = normalizeCurrency(currency);
    const showDisclosure = normalizedCurrency !== null && normalizedCurrency !== 'INR';

    document.querySelectorAll('.cart-drawer [data-cart-currency-note]').forEach((note) => {
      const defaultNote = note.querySelector('[data-cart-default-currency-note]');
      const disclosure = note.querySelector('[data-cart-converted-currency-note]');
      const currencyCode = note.querySelector('[data-cart-display-currency]');

      if (!(defaultNote instanceof HTMLElement) || !(disclosure instanceof HTMLElement)) return;

      defaultNote.hidden = showDisclosure;
      disclosure.hidden = !showDisclosure;

      if (currencyCode) currencyCode.textContent = normalizedCurrency || '';
    });
  };

  const writeCachedGeoCurrency = (currency) => {
    writeStorageValue(localStorage, geoCurrencyKey, {
      currency,
      expiresAt: Date.now() + geoCacheMs,
    });
  };

  const currencyTextPatterns = {
    INR: /₹|\bINR\b|(?:^|\s)Rs\.?\s/i,
    USD: /\$|\bUSD\b/i,
    GBP: /£|\bGBP\b/i,
    EUR: /€|\bEUR\b/i,
    JPY: /[¥￥]|\bJPY\b/i,
  };

  const readConvertedMoneyText = (money) => {
    const copy = money.cloneNode(true);
    copy.querySelectorAll('.barcurr-onmouse-orig-val').forEach((originalPrice) => originalPrice.remove());
    return copy.textContent?.replace(/\s+/g, ' ').trim() || '';
  };

  const priceMarkupMatchesCurrency = (currency) => {
    const moneyElements = Array.from(document.querySelectorAll('span.money')).filter(
      (money) => !money.parentElement?.closest('span.money') && readConvertedMoneyText(money)
    );

    if (moneyElements.length === 0) return false;

    const currencyPattern = currencyTextPatterns[currency];

    return moneyElements.every(
      (money) => money.classList.contains('bacurr-money') && currencyPattern.test(readConvertedMoneyText(money))
    );
  };

  const clearPendingCurrency = (currency) => {
    if (!currency || document.documentElement.dataset.currencyPending === currency) {
      document.documentElement.removeAttribute('data-currency-pending');
    }

    pendingCurrencyCleanup?.();
    pendingCurrencyCleanup = null;
  };

  const beginPendingCurrency = (currency) => {
    const normalizedCurrency = normalizeCurrency(currency);

    if (!normalizedCurrency) return;

    pendingCurrencyCleanup?.();
    document.documentElement.dataset.currencyPending = normalizedCurrency;

    let observer;

    const checkConversion = () => {
      if (document.documentElement.dataset.currencyPending !== normalizedCurrency) {
        pendingCurrencyCleanup?.();
        pendingCurrencyCleanup = null;
        return;
      }

      const novaCurrency = readStoredCurrency(sessionStorage, novaCurrencyKey);

      if (novaCurrency === normalizedCurrency && priceMarkupMatchesCurrency(normalizedCurrency)) {
        updateCartCurrencyDisclosure(normalizedCurrency);
        clearPendingCurrency(normalizedCurrency);
      }
    };

    observer = new MutationObserver(checkConversion);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
      characterData: true,
      childList: true,
      subtree: true,
    });

    const timeoutId = window.setTimeout(() => {
      clearPendingCurrency(normalizedCurrency);
    }, currencyPendingTimeout);

    pendingCurrencyCleanup = () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
    };

    checkConversion();
  };

  const waitForNovaSelector = ({ attempts = 30, delay = 200, timeout = attempts * delay } = {}) =>
    new Promise((resolve) => {
      const existingSelector = document.querySelector('#baCurrSelector');

      if (existingSelector) {
        resolve(existingSelector);
        return;
      }

      const observer = new MutationObserver(() => {
        const selector = document.querySelector('#baCurrSelector');

        if (selector) {
          observer.disconnect();
          window.clearTimeout(timeoutId);
          resolve(selector);
        }
      });

      observer.observe(document.documentElement, { childList: true, subtree: true });

      const timeoutId = window.setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, timeout);
    });

  const selectCurrency = async (currency, waitOptions) => {
    const normalizedCurrency = normalizeCurrency(currency);

    if (!normalizedCurrency) return false;

    const selectionId = ++currencySelectionId;
    const novaSelect = await waitForNovaSelector(waitOptions);

    if (selectionId !== currencySelectionId) return false;

    if (!novaSelect || !novaSelect.querySelector(`option[value="${normalizedCurrency}"]`)) {
      return false;
    }

    novaSelect.value = normalizedCurrency;
    novaSelect.dispatchEvent(new Event('change', { bubbles: true }));

    const novaOption = document.querySelector(`.bacurr-dropdown-choices li[rel="${normalizedCurrency}"]`);

    if (novaOption) {
      novaOption.click();
    }

    updateCartCurrencyDisclosure(normalizedCurrency);

    return true;
  };

  const waitForNovaCurrencyConfirmation = (currency, timeout = 1500) =>
    new Promise((resolve) => {
      const normalizedCurrency = normalizeCurrency(currency);

      if (!normalizedCurrency) {
        resolve(false);
        return;
      }

      const isConfirmed = () => {
        const novaSelectCurrency = normalizeCurrency(document.querySelector('#baCurrSelector')?.value);
        const novaStoredCurrency = readStoredCurrency(sessionStorage, novaCurrencyKey);
        return novaSelectCurrency === normalizedCurrency && novaStoredCurrency === normalizedCurrency;
      };

      if (isConfirmed()) {
        resolve(true);
        return;
      }

      const intervalId = window.setInterval(() => {
        if (!isConfirmed()) return;

        window.clearInterval(intervalId);
        window.clearTimeout(timeoutId);
        resolve(true);
      }, 50);

      const timeoutId = window.setTimeout(() => {
        window.clearInterval(intervalId);
        resolve(false);
      }, timeout);
    });

  const setManualCurrency = async (currency) => {
    const normalizedCurrency = normalizeCurrency(currency);

    if (!normalizedCurrency) return false;

    writeStorageValue(localStorage, manualCurrencyKey, normalizedCurrency);

    if (normalizedCurrency === 'INR') {
      clearPendingCurrency();
    } else {
      beginPendingCurrency(normalizedCurrency);
    }

    dispatchCurrencyUpdate(normalizedCurrency);

    const selected = await selectCurrency(normalizedCurrency, { timeout: 2000 });
    const confirmed = selected ? await waitForNovaCurrencyConfirmation(normalizedCurrency) : false;

    updateCartCurrencyDisclosure(normalizedCurrency);
    dispatchCurrencyUpdate(normalizedCurrency, confirmed);

    return confirmed;
  };

  const reapplySelectedCurrency = async ({ attempts = 6, delay = 250 } = {}) => {
    const currency = readSelectedCurrency();

    if (!currency) return false;

    return selectCurrency(currency, { timeout: attempts * delay });
  };

  const countryToCurrency = (countryCode) => {
    const country = typeof countryCode === 'string' ? countryCode.trim().toUpperCase() : '';

    if (country === 'IN') return 'INR';
    if (country === 'US') return 'USD';
    if (country === 'GB') return 'GBP';
    if (country === 'JP') return 'JPY';
    if (europeanCountries.has(country)) return 'EUR';

    return defaultCurrency;
  };

  const fetchGeoCurrency = async () => {
    const response = await fetch('/browsing_context_suggestions.json', {
      cache: 'no-store',
      credentials: 'omit',
    });

    if (!response.ok) throw new Error('Unable to detect country');

    const browsingContext = await response.json();
    return countryToCurrency(browsingContext.detected_values?.country?.handle);
  };

  const autoSelectCurrency = async () => {
    const manualCurrency = readStoredCurrency(localStorage, manualCurrencyKey);

    if (manualCurrency) {
      writeStorageValue(sessionStorage, novaCurrencyKey, manualCurrency);
      const selected = await selectCurrency(manualCurrency);
      dispatchCurrencyUpdate(manualCurrency, selected);
      return;
    }

    const novaCurrency = readStoredCurrency(sessionStorage, novaCurrencyKey);

    if (novaCurrency) {
      updateCartCurrencyDisclosure(novaCurrency);
      dispatchCurrencyUpdate(novaCurrency, true);
      return;
    }

    const cachedCurrency = readCachedGeoCurrency();

    if (cachedCurrency) {
      const selected = await selectCurrency(cachedCurrency);
      dispatchCurrencyUpdate(cachedCurrency, selected);
      return;
    }

    let currency = defaultCurrency;

    try {
      currency = await fetchGeoCurrency();
    } catch (_error) {
      // Keep the existing default when geolocation is unavailable.
    }

    // A footer choice made while geolocation was in flight is authoritative.
    if (readStoredCurrency(localStorage, manualCurrencyKey)) return;

    writeCachedGeoCurrency(currency);
    const selected = await selectCurrency(currency);
    dispatchCurrencyUpdate(currency, selected);
  };

  const handleCurrencyLinkClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('.currency-link');

    if (!link) return;

    event.preventDefault();

    const currency = normalizeCurrency(link.dataset.currency);

    if (!currency) return;

    await setManualCurrency(currency);
  };

  document.addEventListener('click', handleCurrencyLinkClick);
  document.addEventListener(cartHtmlUpdatedEvent, async () => {
    await reapplySelectedCurrency();
    updateCartCurrencyDisclosure();
  });
  document.addEventListener(currencyMarkupUpdatedEvent, async () => {
    const currency = readSelectedCurrency();

    if (!currency) return;
    if (currency !== 'INR') beginPendingCurrency(currency);

    await reapplySelectedCurrency();
    updateCartCurrencyDisclosure(currency);
  });

  window.IroiroCurrency = {
    ...(window.IroiroCurrency || {}),
    getSelectedCurrency: readDisplayedCurrency,
    getRequestedCurrency: readRequestedCurrency,
    setManualCurrency,
    reapplySelectedCurrency,
    updateCartCurrencyDisclosure,
  };

  const initialPendingCurrency = normalizeCurrency(document.documentElement.dataset.currencyPending);
  if (initialPendingCurrency) beginPendingCurrency(initialPendingCurrency);

  autoSelectCurrency();
})();
